import {
  ArrowPathIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayCircleIcon,
  XCircleIcon,
} from "@heroicons/react/16/solid";
import { ReactElement, useEffect, useState } from "react";

import { CopilotAction } from "../../api/types.gen.ts";
import { useWorkflowExecution } from "../../hooks/use-workflow-execution.ts";
import { Button } from "../catalyst/Button.tsx";
import { NodeSelector } from "./NodeSelector.tsx";

export type ActionGroupProps = {
  actions: CopilotAction[];
  groupName: string;
};

export const RunQueryActionGroup = ({
  actions,
  groupName,
}: ActionGroupProps): null | ReactElement => {
  const { executeMultipleWorkflows, isRunning } = useWorkflowExecution();
  const [hasStarted, setHasStarted] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [currentActions, setCurrentActions] =
    useState<CopilotAction[]>(actions);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    setCurrentActions(actions);
  }, [actions]);

  if (isRejected || currentActions.length === 0) {
    return null;
  }

  const handleRunAll = async (): Promise<void> => {
    setHasStarted(true);

    try {
      const executionPromises = currentActions.map((action) => {
        const { graphId, nodeId, queryAction } = action.params;

        const inputParameters = {
          [queryAction.id]: queryAction.inputParameters || [],
        };
        const selectedRelationships = queryAction.selectedOutputMappingIds
          ? { [queryAction.id]: queryAction.selectedOutputMappingIds }
          : undefined;

        return executeMultipleWorkflows({
          graphId,
          inputParameters,
          invalidateStore: false,
          nodeId,
          queryActions: [queryAction],
          selectedRelationships,
        });
      });

      await Promise.all(executionPromises);
    } catch (error) {
      console.error(
        `Failed to run query actions for group ${groupName}:`,
        error,
      );
    } finally {
      setHasRun(true);
    }
  };

  const handleReject = (): void => {
    setIsRejected(true);
  };

  const removeAction = (actionToRemove: CopilotAction) => (): void => {
    setCurrentActions((prevActions) =>
      prevActions.filter((action) => action !== actionToRemove),
    );
  };

  const isExecuting = isRunning && hasStarted;

  return (
    <div className="mt-1 rounded border border-zinc-600 bg-zinc-900 p-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          <BoltIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-zinc-400" />
          <span className="min-w-0 break-words text-xs font-medium text-zinc-200">
            {groupName} on {currentActions.length} nodes
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button
            disabled={isExecuting}
            onClick={() => setIsCollapsed(!isCollapsed)}
            plain
            size="xs"
          >
            {isCollapsed ? (
              <ChevronDownIcon className="h-2.5 w-2.5" />
            ) : (
              <ChevronUpIcon className="h-2.5 w-2.5" />
            )}
          </Button>
          <Button disabled={isExecuting} onClick={handleReject} plain size="xs">
            <XCircleIcon className="h-2.5 w-2.5" />
          </Button>
          <Button disabled={isExecuting} onClick={handleRunAll} plain size="xs">
            {isExecuting ? (
              <ArrowPathIcon className="h-2.5 w-2.5 animate-spin" />
            ) : hasRun ? (
              <ArrowPathIcon className="h-2.5 w-2.5" />
            ) : (
              <PlayCircleIcon className="h-2.5 w-2.5" />
            )}
          </Button>
        </div>
      </div>
      {!isCollapsed && (
        <div className="flex flex-col gap-1">
          {currentActions.map((action, index) => {
            return (
              <div
                className="flex items-center justify-between text-xs text-zinc-400"
                key={`${action.params.nodeId}-${index}`}
              >
                <NodeSelector nodeId={action.params.nodeId} />
                <Button
                  disabled={isExecuting}
                  onClick={removeAction(action)}
                  plain
                  size="xs"
                >
                  <XCircleIcon className="h-2.5 w-2.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
